import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { type ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmContext, type ConfirmFn, type ConfirmOptions } from './context';

interface OpenState extends ConfirmOptions {
  open: true;
}

/**
 * Mounts a single `<Dialog>` that any descendant can drive via the
 * `useConfirm()` hook. Makes async code a one-liner:
 *
 *     const confirm = useConfirm();
 *     if (!(await confirm({ title: 'Delete?', destructive: true }))) return;
 *     await deleteThing();
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OpenState | null>(null);
  const [busy, setBusy] = useState(false);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);
  const { t } = useTranslation();

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setState({ ...options, open: true });
    });
  }, []);

  const close = (result: boolean) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setState(null);
    setBusy(false);
  };

  const handleConfirm = () => {
    setBusy(true);
    close(true);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={state !== null}
        onClose={() => {
          if (!busy) close(false);
        }}
        aria-labelledby="confirm-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title">
          {state?.title ?? t('common.areYouSure')}
        </DialogTitle>
        {state?.description !== undefined && (
          <DialogContent>
            <DialogContentText component="div">{state.description}</DialogContentText>
          </DialogContent>
        )}
        <DialogActions>
          {!state?.hideCancel && (
            <Button
              onClick={() => {
                close(false);
              }}
              color="inherit"
              disabled={busy}
            >
              {state?.cancelLabel ?? t('common.cancel')}
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={state?.destructive ? 'error' : 'primary'}
            disabled={busy}
            autoFocus
          >
            {state?.confirmLabel ?? t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
